import { watchOnce } from '@vueuse/core';
import type { Tables } from '~/types/database-helpers';
import { type User } from '@supabase/gotrue-js/src';
import { ProfilesService } from '~/lib/services/profiles.service';
import { getStripeSubscription } from '~/utils/stripe/subscriptions';
import type Stripe from 'stripe';
import { getStripeInvoices } from '~/utils/stripe/invoices';
import { usePosthog } from '~/composables/usePosthog';

export type ProfileDetails = Tables<'profiles'> & {
  subscriptions: Tables<'subscriptions'>[];
};

export const useProfileStore = defineStore('profile', () => {
  const config = useRuntimeConfig();
  const { currentPortalPassPriceIds } = useStripeConfig();
  const { $posthog } = useNuxtApp();

  const profile = reactive<ProfileDetails>({
    id: '',
    created_at: null,
    email: '',
    first_name: null,
    last_name: null,
    is_admin: false,
    company: null,
    phone: null,
    stripe_customer_id: null,
    subscriptions: [],
    clientele_type: null,
    utm_campaign: null,
    utm_content: null,
    utm_medium: null,
    utm_source: null,
    utm_term: null,
  });

  const primaryStripeSubscription = ref<Stripe.Subscription | null>(null);
  const invoices = ref<any>(null);
  const invoicesLoading = ref(false);

  const greeting = computed(() =>
    profile.first_name && profile.last_name
      ? profile.first_name + ' ' + profile.last_name
      : profile.first_name && !profile.last_name
      ? ''
      : 'Welcome Back'
  );

  const hasActiveSubscription = computed((state): boolean => {
    /**
     * Admins are considered subscribed
     */
    if (profile.is_admin) return true;
    /**
     * If a subscription has been canceled, we need to honor the subscription until the end of its current term.
     * It sits in cancel-pending status between the time when it is canceled and the end of the term
     * In addition to checking the sub status, we should also make sure that the current_period_end is in the future. sub.current_period_end is a timestamptz in the format 2024-06-27 15:44:46+00
     * If the current_period_end is in the past, we should return false
     */
    if (
      profile.subscriptions.find(
        (sub) =>
          sub.status == 'cancel-pending' &&
          new Date(sub.current_period_end).getTime() > new Date().getTime()
      )
    )
      return true;

    /**
     * If no profile is found (??), or no 'active' subscriptions were found, return false
     */
    if (!profile || !profile.subscriptions.find((sub) => sub.status == 'active')) {
      return false;
    }
    return true;
  });

  const hasActiveTrial = computed((state): boolean => {
    return !!(
      profile &&
      profile.subscriptions.length &&
      profile.subscriptions.find((sub) => {
        const isTrial = sub.status == 'trialing';
        const trialHasNotEnded =
          sub.trial_period_end && new Date(sub.trial_period_end).getTime() > new Date().getTime();
        return isTrial && trialHasNotEnded;
      })
    );
  });

  const hasTrialed = computed((): boolean => {
    return !!(
      profile &&
      profile.subscriptions.length &&
      profile.subscriptions.find((sub) => sub.trial_period_end) && // if any subscription has a trial_period_end, they have used a subscription
      !trialDaysRemaining.value
    );
  });

  const trialDaysRemaining = computed((state): number => {
    const currentTrialingSubscription = profile.subscriptions.find((sub) => {
      const isTrial = sub.status == 'trialing';
      const trialHasNotEnded =
        sub.trial_period_end && new Date(sub.trial_period_end).getTime() > new Date().getTime();
      return isTrial && trialHasNotEnded;
    });
    const difference =
      currentTrialingSubscription?.trial_period_end &&
      new Date(currentTrialingSubscription.trial_period_end).getTime() - new Date().getTime();
    return (difference && Math.ceil(difference / (1000 * 3600 * 24))) || 0; // convert milliseconds to days and round up to nearest integer
  });

  const hasAccess = computed(() => {
    // console.log('');
    // console.log('hasAccess called:');
    // console.log(hasActiveSubscription.value);
    // console.log(hasActiveTrial.value);
    // console.log(trialDaysRemaining.value);
    // console.log('');

    if (isAdmin.value) return true;
    if (hasCurrentPortalPass.value) return true;

    return !!(hasActiveSubscription.value || (hasActiveTrial.value && trialDaysRemaining.value));
  });
  const isAdmin = computed(() => profile.is_admin);

  const sortedSubscriptions = computed((): Tables<'subscriptions'>[] => {
    // Create a shallow copy of the array to avoid mutating the original array
    return profile.subscriptions.slice().sort((a, b) => {
      // Check if either subscription is active; active subscriptions should come first
      if (a.status === 'active' && b.status !== 'active') {
        return -1; // a comes before b
      } else if (b.status === 'active' && a.status !== 'active') {
        return 1; // b comes before a
      } else {
        // If both have the same status, sort by the most recent current_period_start
        return (
          new Date(b.current_period_start).getTime() - new Date(a.current_period_start).getTime()
        );
      }
    });
  });

  const primarySubscription = computed((): Tables<'subscriptions'> | null => {
    const statuses = ['active', 'cancel_pending', 'trialing', 'past_due'];
    for (const status of statuses) {
      const primary = sortedSubscriptions.value.find((sub) => sub.status === status);
      if (primary) return primary;
    }
    return null;
  });

  watch(primarySubscription, async (sub) => {
    try {
      if (
        sub?.stripe_subscription_id === primaryStripeSubscription.value?.id ||
        !sub?.stripe_subscription_id
      )
        return;

      const { data, error } = await getStripeSubscription(sub?.stripe_subscription_id);
      if (error.value) {
        throw error.value;
      }
      if (data.value) {
        primaryStripeSubscription.value = data.value;
      }
    } catch (error) {
      console.error('');
      console.error('error in primarySubscription watcher');
      console.error(error);
      console.error('');
    }
  });

  const portalPassTwentyFourEndDate = new Date('2025-02-01');

  /**
   *  Loop through all invoices to see if any have the product id of the portal pass
   */
  const hasPortalPass = computed((): boolean => {
    if (profile.is_admin) return true;
    const _invoices = invoices.value?.data;
    if (!_invoices || !_invoices.length) return false;
    else {
      return !!_invoices.some((invoice: Stripe.Invoice) => {
        return invoice.lines.data.some((line) => {
          const priceId = line.price?.id;

          const hasIt =
            priceId &&
            [
              config.public.stripePricePortalPass,
              config.public.stripePricePortalPassDiscounted,
              config.public.stripePricePortalPassDiscountedPostSeason,
              config.public.stripePricePortalPassTwentyFive,
              config.public.stripePricePortalPassTwentyFiveDiscounted,
              config.public.stripePriceSoftballPortalPassTwentyFive,
              config.public.stripePriceSoftballPortalPassTwentyFiveDiscounted,
            ].includes(priceId);

          try {
            const posthog = $posthog();
            // Prevent posthog from being called if the user is an admin
            if (posthog && typeof posthog.identify === 'function') {
              posthog.identify(profile.email, { ...profile, ...{ has_portal_pass: !!hasIt } });
            }
          } catch (error) {
            // PostHog not available (likely server-side rendering)
            console.debug('PostHog not available for portal pass tracking');
          }

          // see if the line item price id matches any of the price ids
          return hasIt;
        });
      });
    }
  });

  /**
   *  Check to see if the user has the portal pass for the current season
   */
  const hasCurrentPortalPass = computed((): boolean => {
    if (profile.is_admin) return true;
    const _invoices = invoices.value?.data;
    if (!_invoices || !_invoices.length) return false;
    else {
      return !!_invoices.some((invoice: Stripe.Invoice) => {
        return invoice.lines.data.some((line) => {
          const priceId = line.price?.id;

          return priceId && currentPortalPassPriceIds.value.includes(priceId);
        });
      });
    }
  });

  /* Detect how many years the customer has paid for a portal pass for */
  const yearsPurchased = computed((): number => {
    let years = 0;
    const invoiceData = invoices.value?.data;
    if (!invoiceData) return 0;
    const hasCurrentYearPortalPass = hasCurrentPortalPass.value;
    const hasYear2PortalPass = invoiceData.some((invoice) => {
      return invoice.lines.data.some((line) => {
        return (
          line.price?.id &&
          [
            config.public.stripePricePortalPassTwentySix,
            config.public.stripePriceSoftballPortalPassTwentySix,
          ].includes(line.price.id)
        );
      });
    });
    const hasYear3PortalPass = invoiceData.some((invoice) => {
      return invoice.lines.data.some((line) => {
        return (
          line.price?.id &&
          [
            config.public.stripePricePortalPassTwentySeven,
            config.public.stripePriceSoftballPortalPassTwentySeven,
          ].includes(line.price.id)
        );
      });
    });
    if (hasCurrentYearPortalPass) years++;
    if (hasYear2PortalPass) years++;
    if (hasYear3PortalPass) years++;
    return years;
  });

  const hasCurrentBaseballPortalPass = computed((): boolean => {
    if (profile.is_admin) return true;
    const invoiceData = invoices.value?.data;
    if (!invoiceData) return false;
    return (
      hasCurrentPortalPass.value &&
      invoiceData.some((invoice) => {
        return invoice.lines.data.some((line) => {
          return (
            line.price?.id === config.public.stripePricePortalPassTwentyFive ||
            line.price?.id === config.public.stripePricePortalPassTwentyFiveDiscounted
          );
        });
      })
    );
  });

  const hasCurrentSoftballPortalPass = computed((): boolean => {
    if (profile.is_admin) return true;
    const invoiceData = invoices.value?.data;
    if (!invoiceData) return false;
    return (
      hasCurrentPortalPass.value &&
      invoiceData.some((invoice) => {
        return invoice.lines.data.some((line) => {
          return (
            line.price?.id === config.public.stripePriceSoftballPortalPassTwentyFive ||
            line.price?.id === config.public.stripePriceSoftballPortalPassTwentyFiveDiscounted
          );
        });
      })
    );
  });

  const twentyFourPortalPassHasExpired = computed((): boolean => {
    return new Date().getTime() > portalPassTwentyFourEndDate.getTime();
  });
  /**
   * If user has 2024 portal pass, but not 2025 one and we are past the expiration date, this should return true
   */
  const hasExpiredPortalPass = computed((): boolean => {
    if (profile.is_admin) return true;
    const _invoices = invoices.value?.data;
    if (!_invoices || !_invoices.length) return false;
    return (
      hasPortalPass.value && !hasCurrentPortalPass.value && twentyFourPortalPassHasExpired.value
    );
  });

  const fetchInvoices = async () => {
    if (!profile.stripe_customer_id) return null;

    try {
      invoicesLoading.value = true;
      const result = await getStripeInvoices(profile.stripe_customer_id, {
        status: 'paid',
        limit: 100,
      });
      invoices.value = result;
      return result;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return null;
    } finally {
      invoicesLoading.value = false;
    }
  };

  const fetchProfile = async (user: User): Promise<ProfileDetails | null> => {
    try {
      if (!user) return null;
      const supabase = useSupabaseClient();
      const profileService = new ProfilesService(supabase);
      const { data } = await profileService.getProfileDetails(user.id);
      if (data) {
        setProfile(data);
      }
      return data;
    } catch (error: any) {
      console.error(error);
      return null;
    }
  };

  const setProfile = (data: ProfileDetails) => {
    Object.assign(profile, {}, data);
  };

  const resetProfile = () => {
    Object.assign(
      profile,
      {},
      {
        id: '',
        created_at: null,
        email: '',
        first_name: null,
        last_name: null,
        is_admin: false,
        company: null,
        phone: null,
        stripe_customer_id: null,
        subscriptions: [],
      }
    );
  };

  const canAccessSport = (sport: Sport) => {
    return sport === 'Baseball'
      ? hasCurrentBaseballPortalPass.value
      : hasCurrentSoftballPortalPass.value;
  };

  return {
    profile,
    greeting,
    hasActiveSubscription,
    hasActiveTrial,
    hasTrialed,
    primarySubscription,
    trialDaysRemaining,
    hasAccess,
    fetchProfile,
    setProfile,
    resetProfile,
    primaryStripeSubscription,
    hasPortalPass,
    hasCurrentPortalPass,
    hasCurrentBaseballPortalPass,
    hasCurrentSoftballPortalPass,
    canAccessSport,
    invoices,
    invoicesLoading,
    fetchInvoices,
    isAdmin,
    hasExpiredPortalPass,
    portalPassTwentyFourEndDate,
    yearsPurchased,
  };
});
